/**
 * Night Shift Declaration History Dialog
 *
 * Displays an ordered audit trail of status changes for a single declaration
 * in a modal dialog. Each entry shows the status, who made the change, when,
 * and an optional note.
 *
 * @module night-shift-declarations/NightShiftDeclarationHistory
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box,
    Chip,
    Typography,
} from '@mui/material';
import { getNightShiftDeclarationHistory, NightShiftDeclarationHistoryRecord } from '../dataProvider';

/**
 * Props for the NightShiftDeclarationHistory component.
 */
interface NightShiftDeclarationHistoryProps {
    /** The ID of the declaration to show history for. */
    declarationId: number;
    /** Whether the dialog is open. */
    open: boolean;
    /** Callback to close the dialog. */
    onClose: () => void;
}

/**
 * Returns a status chip color based on the history entry status.
 *
 * @param status - The declaration status string.
 * @returns A MUI chip color name.
 */
function getStatusColor (status: string): 'success' | 'default' | 'warning' | 'error' {
    switch (status) {
        case 'Approved': return 'success';
        case 'Cancelled': return 'error';
        case 'Submitted': return 'warning';
        default: return 'default';
    }
}

export const NightShiftDeclarationHistory: React.FC<NightShiftDeclarationHistoryProps> = ({
    declarationId,
    open,
    onClose,
}) => {
    const [history, setHistory] = useState<NightShiftDeclarationHistoryRecord[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;

        const fetchHistory = async () => {
            setLoading(true);
            try {
                const data = await getNightShiftDeclarationHistory(declarationId);
                setHistory(data);
            } catch (error) {
                console.error('Error fetching declaration history:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [declarationId, open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Declaration History (#{declarationId})</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : history.length === 0 ? (
                    <Typography color="text.secondary" align="center" py={2}>
                        No history entries found.
                    </Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Changed By</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Note</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map((entry) => (
                                    <TableRow key={entry.id}>
                                        <TableCell>
                                            <Chip
                                                label={entry.status}
                                                color={getStatusColor(entry.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{entry.changed_by_name || entry.changed_by}</TableCell>
                                        <TableCell>
                                            {entry.changed_date
                                                ? new Date(entry.changed_date).toLocaleString()
                                                : '—'}
                                        </TableCell>
                                        <TableCell>{entry.note || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
